// Lightweight live pose detector for the capture coach. Separate from the
// analysis engine (poseEngine.ts): it runs single-frame detection on the live
// webcam preview so we can guide framing before recording. Fault-tolerant —
// if the model can't load, callers simply get null and the coach hides itself.

import {
  FilesetResolver,
  PoseLandmarker,
  type PoseLandmarkerResult,
} from "@mediapipe/tasks-vision";
import type { Landmark, PoseFrame } from "../pose/landmarks";

const WASM_PATH = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/wasm";
const MODEL_PATH = "/models/pose_landmarker_full.task";

export class LivePoseDetector {
  private landmarker: PoseLandmarker | null = null;
  private ready = false;

  async init(): Promise<boolean> {
    try {
      const fileset = await FilesetResolver.forVisionTasks(WASM_PATH);
      this.landmarker = await PoseLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MODEL_PATH, delegate: "GPU" },
        runningMode: "VIDEO",
        numPoses: 1,
        minPoseDetectionConfidence: 0.5,
        minPosePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });
      this.ready = true;
    } catch {
      this.ready = false;
    }
    return this.ready;
  }

  isReady(): boolean {
    return this.ready;
  }

  detect(video: HTMLVideoElement, tMs: number): PoseFrame | null {
    if (!this.landmarker || video.readyState < 2 || !video.videoWidth) return null;
    try {
      return toPoseFrame(this.landmarker.detectForVideo(video, tMs), tMs);
    } catch {
      return null;
    }
  }

  close(): void {
    try {
      this.landmarker?.close();
    } catch {
      /* ignore */
    }
    this.landmarker = null;
    this.ready = false;
  }
}

function toPoseFrame(result: PoseLandmarkerResult, tMs: number): PoseFrame | null {
  const lms = result.landmarks?.[0];
  if (!lms || lms.length < 33) return null;
  const landmarks: Landmark[] = lms.map((p) => ({
    x: p.x,
    y: p.y,
    z: p.z ?? 0,
    visibility: p.visibility ?? 1,
  }));
  return { tMs, landmarks };
}

/** Mean luma (0..1) of a video frame via a tiny downscaled canvas. */
export function frameBrightness(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
): number | null {
  if (video.readyState < 2 || !video.videoWidth) return null;
  const w = 32, h = 18;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  try {
    ctx.drawImage(video, 0, 0, w, h);
    const data = ctx.getImageData(0, 0, w, h).data;
    let sum = 0;
    for (let i = 0; i < data.length; i += 4) {
      sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }
    return sum / (w * h) / 255;
  } catch {
    return null;
  }
}
